// ============================================================================
// SYNC STATE MACHINE
// ============================================================================
// Manages the synchronisation lifecycle for a single vault.
//
// Transitions are expressed as explicit methods.  TypeScript's parameter types
// restrict *which* states may trigger each method at compile time.  Additional
// runtime guards protect transitions that accept a broad union (e.g. `pause`,
// `fail`) so that only truly valid source states are allowed at runtime too.
//
// Usage:
//   const machine = new SyncStateMachine(vaultId);
//   machine.on((event) => { ... }); // subscribe to every transition
//   machine.initialiseSyncEnabled({ localId, serverId, serverLastModified, lastSyncDate });
// ============================================================================

import type {
  SyncState,
  SyncStatus,
  SyncContext,
  PausableStatus,
  SyncOperation,
  SyncTransitionEvent,
  SyncTransitionListener,
  InitialisingState,
  LocalOnlyState,
  IdleState,
  DirtyState,
  RegisteringState,
  CheckingState,
  FetchingState,
  UploadingState,
  MergingState,
  PausedState,
  UnregisteringState,
  ErrorState,
} from './types';

// ============================================================================
// Helpers
// ============================================================================

function now(): string {
  return new Date().toISOString();
}

/** Statuses that can be paused when connectivity is lost */
const PAUSABLE_STATUSES: ReadonlySet<SyncStatus> = new Set<PausableStatus>([
  'idle',
  'dirty',
  'checking',
  'fetching',
  'uploading',
  'merging',
  'registering',
]);

function isPausable(state: SyncState): state is SyncState & { status: PausableStatus } {
  return PAUSABLE_STATUSES.has(state.status);
}

/** Statuses from which the user can trigger unregistration */
const UNREGISTERABLE_STATUSES: ReadonlySet<SyncStatus> = new Set<SyncStatus>([
  'idle',
  'dirty',
  'error',
]);

function isUnregisterable(state: SyncState): state is IdleState | DirtyState | ErrorState {
  return UNREGISTERABLE_STATUSES.has(state.status);
}

/** Statuses from which a new transition to Dirty is permitted */
const DIRTIFIABLE_STATUSES: ReadonlySet<SyncStatus> = new Set<SyncStatus>([
  'idle',
  'dirty',
]);

// ============================================================================
// Guard assertions — throw at runtime when a caller attempts an illegal move
// ============================================================================

function assertStatus<T extends SyncState>(
  state: SyncState,
  expected: T['status'] | ReadonlyArray<T['status']>,
  operation: string,
): asserts state is T {
  const allowed = Array.isArray(expected) ? expected : [expected];
  if (!allowed.includes(state.status as T['status'])) {
    throw new SyncTransitionError(
      operation,
      state.status,
      allowed as SyncStatus[],
    );
  }
}

// ============================================================================
// Custom error
// ============================================================================

export class SyncTransitionError extends Error {
  constructor(
    public readonly operation: string,
    public readonly currentStatus: SyncStatus,
    public readonly allowedStatuses: SyncStatus[],
  ) {
    super(
      `[SyncStateMachine] Cannot perform "${operation}" from state "${currentStatus}". ` +
        `Allowed source states: [${allowedStatuses.join(', ')}].`,
    );
    this.name = 'SyncTransitionError';
  }
}

// ============================================================================
// State machine
// ============================================================================

export class SyncStateMachine {
  private _state: SyncState;
  private readonly _listeners: Set<SyncTransitionListener> = new Set();

  constructor(private readonly vaultId: string) {
    this._state = { status: 'initialising' } satisfies InitialisingState;
  }

  // --------------------------------------------------------------------------
  // Public accessors
  // --------------------------------------------------------------------------

  get state(): SyncState {
    return this._state;
  }

  get status(): SyncStatus {
    return this._state.status;
  }

  // --------------------------------------------------------------------------
  // Event subscription
  // --------------------------------------------------------------------------

  /**
   * Subscribe to all state transitions.
   * Returns an unsubscribe function.
   */
  on(listener: SyncTransitionListener): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  // --------------------------------------------------------------------------
  // Transitions: Initialising → *
  // --------------------------------------------------------------------------

  /**
   * Vault has been loaded from localStorage and has never had sync enabled.
   * `Initialising` → `LocalOnly`
   */
  initialiseLocalOnly(localId: string): LocalOnlyState {
    assertStatus<InitialisingState>(this._state, 'initialising', 'initialiseLocalOnly');
    return this._transition({ status: 'local-only', localId });
  }

  /**
   * Vault has been loaded and sync is enabled with an up-to-date server copy.
   * `Initialising` → `Idle`
   */
  initialiseSyncEnabled(context: SyncContext): IdleState {
    assertStatus<InitialisingState>(this._state, 'initialising', 'initialiseSyncEnabled');
    return this._transition({ status: 'idle', ...context });
  }

  /**
   * Vault has been loaded, sync is enabled, but local changes exist that
   * postdate the last sync.
   * `Initialising` → `Dirty`
   */
  initialiseDirty(context: SyncContext & { localChangedAt: string }): DirtyState {
    assertStatus<InitialisingState>(this._state, 'initialising', 'initialiseDirty');
    return this._transition({ status: 'dirty', ...context });
  }

  // --------------------------------------------------------------------------
  // Transitions: LocalOnly → Registering
  // --------------------------------------------------------------------------

  /**
   * User enables sync for a local-only vault; begin the registration process.
   * `LocalOnly` → `Registering`
   */
  register(): RegisteringState {
    assertStatus<LocalOnlyState>(this._state, 'local-only', 'register');
    const { localId } = this._state;
    return this._transition({ status: 'registering', localId });
  }

  /**
   * Server accepted the registration.
   * `Registering` → `Idle`
   */
  registered(payload: { serverId: string; serverLastModified: string; lastSyncDate: string }): IdleState {
    assertStatus<RegisteringState>(this._state, 'registering', 'registered');
    const { localId } = this._state;
    return this._transition({
      status: 'idle',
      localId,
      serverId: payload.serverId,
      serverLastModified: payload.serverLastModified,
      lastSyncDate: payload.lastSyncDate,
    });
  }

  // --------------------------------------------------------------------------
  // Transitions: Idle | Dirty → Checking
  // --------------------------------------------------------------------------

  /**
   * Begin querying the server for a newer version.
   * `Idle` | `Dirty` → `Checking`
   */
  check(): CheckingState {
    assertStatus<IdleState | DirtyState>(
      this._state,
      ['idle', 'dirty'],
      'check',
    );
    const { localId, serverId, serverLastModified } = this._state;
    return this._transition({ status: 'checking', localId, serverId, serverLastModified });
  }

  // --------------------------------------------------------------------------
  // Transitions: Checking → *
  // --------------------------------------------------------------------------

  /**
   * Server has a newer version; begin downloading it.
   * `Checking` → `Fetching`
   */
  fetch(): FetchingState {
    assertStatus<CheckingState>(this._state, 'checking', 'fetch');
    const { localId, serverId, serverLastModified } = this._state;
    return this._transition({ status: 'fetching', localId, serverId, serverLastModified });
  }

  /**
   * Local version is newer than the server; begin uploading.
   * `Checking` → `Uploading`
   */
  upload(): UploadingState {
    assertStatus<CheckingState>(this._state, 'checking', 'upload');
    const { localId, serverId, serverLastModified } = this._state;
    return this._transition({ status: 'uploading', localId, serverId, serverLastModified });
  }

  /**
   * Both local and server have changed since the last sync.
   * `Checking` → `Merging`
   */
  conflict(payload: { localChangedAt: string; serverChangedAt: string }): MergingState {
    assertStatus<CheckingState>(this._state, 'checking', 'conflict');
    const { localId, serverId, serverLastModified } = this._state;
    return this._transition({
      status: 'merging',
      localId,
      serverId,
      serverLastModified,
      localChangedAt: payload.localChangedAt,
      serverChangedAt: payload.serverChangedAt,
    });
  }

  /**
   * Server version matches local; nothing to do.
   * `Checking` → `Idle`
   */
  upToDate(payload: { lastSyncDate: string; serverLastModified: string }): IdleState {
    assertStatus<CheckingState>(this._state, 'checking', 'upToDate');
    const { localId, serverId } = this._state;
    return this._transition({ status: 'idle', localId, serverId, ...payload });
  }

  // --------------------------------------------------------------------------
  // Transitions: Fetching → *
  // --------------------------------------------------------------------------

  /**
   * Download complete; vault is now in sync.
   * `Fetching` → `Idle`
   */
  fetched(payload: { serverLastModified: string; lastSyncDate: string }): IdleState {
    assertStatus<FetchingState>(this._state, 'fetching', 'fetched');
    const { localId, serverId } = this._state;
    return this._transition({
      status: 'idle',
      localId,
      serverId,
      serverLastModified: payload.serverLastModified,
      lastSyncDate: payload.lastSyncDate,
    });
  }

  // --------------------------------------------------------------------------
  // Transitions: Uploading → *
  // --------------------------------------------------------------------------

  /**
   * Upload accepted by the server; vault is now in sync.
   * `Uploading` → `Idle`
   */
  uploaded(payload: { serverLastModified: string; lastSyncDate: string }): IdleState {
    assertStatus<UploadingState>(this._state, 'uploading', 'uploaded');
    const { localId, serverId } = this._state;
    return this._transition({
      status: 'idle',
      localId,
      serverId,
      serverLastModified: payload.serverLastModified,
      lastSyncDate: payload.lastSyncDate,
    });
  }

  /**
   * Server rejected the upload with a 412 (Precondition Failed): the server
   * copy changed between our last check and the upload attempt.
   * `Uploading` → `Merging`
   */
  uploadConflicted(payload: { localChangedAt: string; serverChangedAt: string }): MergingState {
    assertStatus<UploadingState>(this._state, 'uploading', 'uploadConflicted');
    const { localId, serverId, serverLastModified } = this._state;
    return this._transition({
      status: 'merging',
      localId,
      serverId,
      serverLastModified,
      localChangedAt: payload.localChangedAt,
      serverChangedAt: payload.serverChangedAt,
    });
  }

  // --------------------------------------------------------------------------
  // Transitions: Merging → *
  // --------------------------------------------------------------------------

  /**
   * User chose to keep the local version; re-attempt upload.
   * `Merging` → `Uploading`
   */
  resolveWithLocal(): UploadingState {
    assertStatus<MergingState>(this._state, 'merging', 'resolveWithLocal');
    const { localId, serverId, serverLastModified } = this._state;
    return this._transition({ status: 'uploading', localId, serverId, serverLastModified });
  }

  /**
   * User chose to accept the server version; begin download.
   * `Merging` → `Fetching`
   */
  resolveWithRemote(): FetchingState {
    assertStatus<MergingState>(this._state, 'merging', 'resolveWithRemote');
    const { localId, serverId, serverLastModified } = this._state;
    return this._transition({ status: 'fetching', localId, serverId, serverLastModified });
  }

  // --------------------------------------------------------------------------
  // Transitions: Idle | Dirty — mark local changes
  // --------------------------------------------------------------------------

  /**
   * A local vault change has been made.  Transitions `Idle` → `Dirty`, or
   * updates `localChangedAt` if already `Dirty`.
   *
   * Runtime guard: only permitted from `idle` or `dirty`.
   */
  markDirty(localChangedAt: string = now()): DirtyState {
    if (!DIRTIFIABLE_STATUSES.has(this._state.status)) {
      throw new SyncTransitionError('markDirty', this._state.status, ['idle', 'dirty']);
    }
    const s = this._state as IdleState | DirtyState;
    return this._transition({
      status: 'dirty',
      localId: s.localId,
      serverId: s.serverId,
      serverLastModified: s.serverLastModified,
      lastSyncDate: s.lastSyncDate,
      localChangedAt,
    });
  }

  // --------------------------------------------------------------------------
  // Transitions: any pausable → Paused
  // --------------------------------------------------------------------------

  /**
   * Device went offline (or connectivity was lost mid-operation).
   * Valid from any `PausableStatus` state.
   *
   * Runtime guard enforces the source state because the parameter type alone
   * (`SyncState`) cannot narrow to only pausable states.
   */
  pause(): PausedState {
    if (!isPausable(this._state)) {
      throw new SyncTransitionError(
        'pause',
        this._state.status,
        Array.from(PAUSABLE_STATUSES) as SyncStatus[],
      );
    }
    const base = this._state;
    const localId = (base as { localId: string }).localId;
    const serverId = 'serverId' in base ? (base as { serverId: string }).serverId : undefined;
    const hasDirtyChanges = base.status === 'dirty';

    return this._transition({
      status: 'paused',
      localId,
      serverId,
      pausedFrom: base.status as PausableStatus,
      hasDirtyChanges,
    });
  }

  /**
   * Device is back online.  Resumes to the most appropriate state:
   * - `dirty` if there were uncommitted local changes before pausing
   * - `idle`  otherwise (triggers a fresh check via normal polling)
   *
   * `Paused` → `Dirty` | `Idle`
   */
  resume(context: SyncContext): IdleState | DirtyState {
    assertStatus<PausedState>(this._state, 'paused', 'resume');
    const { hasDirtyChanges } = this._state;

    if (hasDirtyChanges) {
      return this._transition({
        status: 'dirty',
        ...context,
        localChangedAt: now(),
      });
    }
    return this._transition({ status: 'idle', ...context });
  }

  // --------------------------------------------------------------------------
  // Transitions: Idle | Dirty | Error → Unregistering
  // --------------------------------------------------------------------------

  /**
   * User disables sync; send delete request to the server.
   * Valid from `idle`, `dirty`, or `error`.
   *
   * Runtime guard required because the allowed source states form a broad union
   * that TypeScript cannot express as a single parameter type without overloads.
   */
  unregister(): UnregisteringState {
    if (!isUnregisterable(this._state)) {
      throw new SyncTransitionError(
        'unregister',
        this._state.status,
        Array.from(UNREGISTERABLE_STATUSES) as SyncStatus[],
      );
    }
    const localId = this._state.localId;
    // Both IdleState and DirtyState have serverId; ErrorState has optional serverId
    const serverId =
      this._state.status === 'error'
        ? (this._state.serverId ?? '')
        : this._state.serverId;

    return this._transition({ status: 'unregistering', localId, serverId });
  }

  /**
   * Server confirmed deletion.
   * `Unregistering` → `LocalOnly`
   */
  unregistered(): LocalOnlyState {
    assertStatus<UnregisteringState>(this._state, 'unregistering', 'unregistered');
    const { localId } = this._state;
    return this._transition({ status: 'local-only', localId });
  }

  // --------------------------------------------------------------------------
  // Transitions: any active state → Error
  // --------------------------------------------------------------------------

  /**
   * An async operation failed.  Records the failing operation, the error
   * message, and the status that was active at the time.
   *
   * Valid from any state that has a `localId` (i.e. not `initialising`).
   * Runtime guard enforces this.
   */
  fail(operation: SyncOperation, message: string): ErrorState {
    if (this._state.status === 'initialising') {
      throw new SyncTransitionError('fail', this._state.status, [
        'local-only', 'idle', 'dirty', 'registering', 'checking',
        'fetching', 'uploading', 'merging', 'paused', 'unregistering',
      ]);
    }
    const base = this._state as Exclude<SyncState, InitialisingState>;
    const serverId = 'serverId' in base ? (base as { serverId?: string }).serverId : undefined;

    return this._transition({
      status: 'error',
      localId: base.localId,
      serverId,
      failedOperation: operation,
      message,
      previousStatus: base.status,
    });
  }

  /**
   * Dismiss the error and return to `Idle` (the consumer is responsible for
   * providing fresh sync context if the previous state was before any sync had
   * occurred, e.g. after a failed registration).
   *
   * `Error` → `Idle`
   */
  recover(context: SyncContext): IdleState {
    assertStatus<ErrorState>(this._state, 'error', 'recover');
    return this._transition({ status: 'idle', ...context });
  }

  /**
   * Dismiss the error and return to `LocalOnly` — used when the error occurred
   * during registration and no server ID was ever assigned.
   *
   * `Error` → `LocalOnly`
   */
  recoverToLocalOnly(): LocalOnlyState {
    assertStatus<ErrorState>(this._state, 'error', 'recoverToLocalOnly');
    return this._transition({ status: 'local-only', localId: this._state.localId });
  }

  // --------------------------------------------------------------------------
  // Internal transition engine
  // --------------------------------------------------------------------------

  private _transition<T extends SyncState>(next: T): T {
    const event: SyncTransitionEvent = {
      vaultId: this.vaultId,
      from: this._state,
      to: next,
      timestamp: now(),
    };

    this._state = next;
    this._emit(event);
    return next;
  }

  private _emit(event: SyncTransitionEvent): void {
    for (const listener of this._listeners) {
      try {
        listener(event);
      } catch (err) {
        // Listeners must not crash the machine
        console.error('[SyncStateMachine] Unhandled error in transition listener:', err);
      }
    }
  }
}
